// identity-groups.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserGroup {
  id: number;
  name: string;
  authenticationRequirement: string;
  policyBaseline: {
    id: number;
    name: string;
    description: string;
    targetType: string;
    scopes: string[];
    status: string;
    createdDate: string;
  } | null;
  membershipEvaluation: string;
  createdDate: string;
  memberCount?: number;
}

export interface CreateUserGroupRequest {
  name: string;
  authenticationRequirement: string;
  policyId: number;
}

export interface UpdateUserGroupRequest {
  name: string;
  authenticationRequirement: string;
  policyId: number;
}

export interface PolicyDropdownItem {
  id: number;
  name: string;
  scopes: string[];
  targetType?:string
}

// Member related interfaces
export interface User {
  id: number;
  username: string;
  email: string;
  mobile: string;
  name: string;
  tenantId: number;
  createdDate: string;
  enabled: boolean;
}

export interface GroupMembersResponse {
  groupId: number;
  groupName: string;
  memberCount: number;
  members: User[];
}

export interface AddMemberRequest {
  userId: number;
}

@Injectable({
  providedIn: 'root'
})
export class IdentityGroupsService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  getUserGroups(): Observable<UserGroup[]> {
    return this.http.get<UserGroup[]>(`${this.baseUrl}/admin/user-groups`);
  }

  getUserGroup(id: number): Observable<UserGroup> {
    return this.http.get<UserGroup>(`${this.baseUrl}/admin/user-groups/${id}`);
  }

  createUserGroup(payload: CreateUserGroupRequest): Observable<UserGroup> {
    return this.http.post<UserGroup>(`${this.baseUrl}/admin/user-groups`, payload);
  }

  updateUserGroup(id: number, payload: UpdateUserGroupRequest): Observable<UserGroup> {
    return this.http.put<UserGroup>(`${this.baseUrl}/admin/user-groups/${id}`, payload);
  }

  getPoliciesDropdown(): Observable<PolicyDropdownItem[]> {
    return this.http.get<PolicyDropdownItem[]>(`${this.baseUrl}/admin/access-policies/dropdown`);
  }

  getGroupMembers(groupId: number): Observable<GroupMembersResponse> {
    return this.http.get<GroupMembersResponse>(`${this.baseUrl}/admin/user-groups/${groupId}/members`);
  }

  getAvailableUsers(groupId: number, query: string = ''): Observable<User[]> {
    let params = new HttpParams();
    if (query) {
      params = params.set('query', query);
    }
    return this.http.get<User[]>(`${this.baseUrl}/admin/user-groups/${groupId}/available-users`, { params });
  }

  addMemberToGroup(groupId: number, userId: number): Observable<User> {
    const payload: AddMemberRequest = { userId };
    return this.http.post<User>(`${this.baseUrl}/admin/user-groups/${groupId}/members`, payload);
  }

  removeMemberFromGroup(groupId: number, userId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/user-groups/${groupId}/members/${userId}`);
  }
}